import React, { useEffect, useRef } from 'react';

interface RecordingWaveformProps {
	stream: MediaStream | null;
	isPaused: boolean;
	durationMs: number;
	onPauseToggle: () => void;
}

const RecordingWaveform: React.FC<RecordingWaveformProps> = ({
	stream,
	isPaused,
	durationMs,
	onPauseToggle,
}) => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const audioContextRef = useRef<AudioContext | null>(null);
	const lastDrawTimeRef = useRef<number>(0);
	const targetFPSRef = useRef<number>(20); // Target 20 FPS for smoother performance on older devices
	const frameIntervalRef = useRef<number>(1000 / targetFPSRef.current);

	// Initialize audio analysis
	useEffect(() => {
		if (!stream) {
			return;
		}

		try {
			const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
			audioContextRef.current = audioContext;

			const source = audioContext.createMediaStreamSource(stream);
			const analyser = audioContext.createAnalyser();
			analyser.fftSize = 256;
			analyser.smoothingTimeConstant = 0.8;
			analyserRef.current = analyser;

			source.connect(analyser);

			const bufferLength = analyser.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);
			const samples = 50; // Reduced from 100 for better performance
			const blockSize = Math.floor(bufferLength / samples);

			// Start animation loop - draw directly to canvas to avoid React re-renders
			const updateWaveform = () => {
				const now = Date.now();
				const shouldDraw = !isPaused && now - lastDrawTimeRef.current >= frameIntervalRef.current;

				if (shouldDraw && analyserRef.current && canvasRef.current) {
					analyserRef.current.getByteFrequencyData(dataArray);

					const canvas = canvasRef.current;
					const ctx = canvas.getContext('2d');
					if (!ctx) {
						animationFrameRef.current = requestAnimationFrame(updateWaveform);
						return;
					}

					const width = canvas.width;
					const height = canvas.height;
					const barWidth = width / samples;

					// Clear canvas once
					ctx.clearRect(0, 0, width, height);

					// Set fill style once (more efficient)
					ctx.fillStyle = isPaused ? '#d8b4fe' : '#a855f7';

					// Draw all bars in a single batch
					for (let i = 0; i < samples; i++) {
						// Calculate average for this bar
						let sum = 0;
						const startIdx = i * blockSize;
						const endIdx = Math.min(startIdx + blockSize, bufferLength);
						for (let j = startIdx; j < endIdx; j++) {
							sum += dataArray[j];
						}
						const average = sum / (endIdx - startIdx);
						const normalizedLevel = (average / 255) * 100;

						// Draw bar
						const x = i * barWidth;
						const barHeight = (normalizedLevel / 100) * height;
						const y = (height - barHeight) / 2;

						ctx.fillRect(x, y, barWidth - 1, barHeight);
					}

					lastDrawTimeRef.current = now;
				}

				animationFrameRef.current = requestAnimationFrame(updateWaveform);
			};

			updateWaveform();
		} catch (error) {
			console.error('Error initializing audio analysis:', error);
		}

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
			}
		};
	}, [stream, isPaused]);

	// Clear canvas when paused or stream stops
	useEffect(() => {
		if (!canvasRef.current) return;

		if (!stream || isPaused) {
			const canvas = canvasRef.current;
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.clearRect(0, 0, canvas.width, canvas.height);
			}
		}
	}, [stream, isPaused]);

	// Format time as MM:SS
	const formatTime = (ms: number) => {
		const totalSeconds = Math.floor(ms / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
	};

	return (
		<div className="flex min-w-[200px] items-center gap-2 ">
			<div className="relative h-10 min-w-[120px] flex-1">
				<canvas ref={canvasRef} width={200} height={40} className="block h-full w-full" />
			</div>

			<div className="min-w-[35px] flex-shrink-0 text-right text-xs text-gray-400">
				{formatTime(durationMs)}
			</div>
		</div>
	);
};

export default RecordingWaveform;
